import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import Money from "./Money";

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    "text-sm transition-colors",
    isActive ? "text-indigo-600 font-medium" : "text-slate-600 hover:text-slate-900",
  ].join(" ");
}

function mobileNavLinkClass({ isActive }: { isActive: boolean }): string {
  return [
    "block w-full px-4 py-3 text-base transition-colors",
    isActive ? "text-indigo-600 font-medium bg-indigo-50" : "text-slate-700 hover:bg-slate-50",
  ].join(" ");
}

function ProfileDropdown({ email, onLogout, isAdmin }: { email: string; onLogout: () => void; isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="true"
        className="text-sm font-medium text-slate-600 hover:text-slate-900 flex items-center gap-1"
      >
        {t("nav.profile")}
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-56 rounded-md border border-slate-200 bg-white py-1 shadow-lg z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-900 truncate">{email}</p>
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/settings"); }}
            role="menuitem"
            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t("nav.settings")}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/admin"); }}
              role="menuitem"
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              {t("nav.admin")}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); onLogout(); }}
            role="menuitem"
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            {t("nav.logOut")}
          </button>
        </div>
      )}
    </div>
  );
}

function MobileDrawer({
  open,
  onClose,
  user,
  onLogout,
}: {
  open: boolean;
  onClose: () => void;
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  onLogout: () => void;
}) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
      onClose();
    },
    [navigate, onClose],
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={[
          "fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 md:hidden",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("nav.menu")}
        className={[
          "fixed top-0 right-0 z-50 h-full w-72 bg-white shadow-xl transition-transform duration-300 ease-in-out md:hidden",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        {/* Close button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <span className="text-sm font-semibold text-slate-900 truncate">{user.email}</span>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            aria-label="Close menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation items */}
        <nav className="flex flex-col py-2 overflow-y-auto h-[calc(100%-57px)]">
          <NavLink to="/dashboard" className={mobileNavLinkClass} onClick={onClose}>
            {t("nav.dashboard")}
          </NavLink>
          <NavLink to="/upload" className={mobileNavLinkClass} onClick={onClose}>
            {t("nav.addBills")}
          </NavLink>
          <NavLink to="/batches" className={mobileNavLinkClass} onClick={onClose}>
            {t("nav.myBills")}
          </NavLink>

          {/* Wallet pill */}
          <Link
            to="/wallet"
            onClick={onClose}
            className="mx-4 my-2 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            {t("nav.wallet")}{" "}
            <Money
              cents={Math.max(0, user.wallet.balance_cents - user.wallet.held_cents)}
              currency={user.wallet.currency}
            />
            {user.wallet.held_cents > 0 && (
              <span className="text-slate-500">
                ({t("walletPill.held")} <Money cents={user.wallet.held_cents} currency={user.wallet.currency} />)
              </span>
            )}
          </Link>

          {/* Top up */}
          <Link
            to="/wallet#topup"
            onClick={onClose}
            className="mx-4 my-1 text-center rounded-full bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            {t("nav.topUp")}
          </Link>

          <div className="my-2 border-t border-slate-200" />

          {/* Profile section items */}
          <button
            type="button"
            onClick={() => handleNavigate("/settings")}
            className="w-full text-left px-4 py-3 text-base text-slate-700 hover:bg-slate-50"
          >
            {t("nav.settings")}
          </button>
          {(user.is_admin ?? false) && (
            <button
              type="button"
              onClick={() => handleNavigate("/admin")}
              className="w-full text-left px-4 py-3 text-base text-slate-700 hover:bg-slate-50"
            >
              Admin Panel
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onLogout();
              onClose();
            }}
            className="w-full text-left px-4 py-3 text-base text-red-600 hover:bg-red-50"
          >
            {t("nav.logOut")}
          </button>

          <div className="my-2 border-t border-slate-200" />

          {/* Language switcher */}
          <div className="px-4 py-3">
            <LanguageSwitcher />
          </div>
        </nav>
      </div>
    </>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [mobileOpen, setMobileOpen] = useState(false);

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="text-lg font-semibold text-indigo-600">
          {t("brand")}
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center gap-5">
          {user ? (
            <>
              <NavLink to="/dashboard" className={navLinkClass}>
                {t("nav.dashboard")}
              </NavLink>
              <NavLink to="/upload" className={navLinkClass}>
                {t("nav.addBills")}
              </NavLink>
              <NavLink to="/batches" className={navLinkClass}>
                {t("nav.myBills")}
              </NavLink>
              <Link
                to="/wallet"
                title={
                  user.wallet.held_cents > 0
                    ? t("walletPill.spendableWithHeld")
                    : t("walletPill.available")
                }
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
              >
                {t("nav.wallet")}{" "}
                <Money
                  cents={Math.max(
                    0,
                    user.wallet.balance_cents - user.wallet.held_cents,
                  )}
                  currency={user.wallet.currency}
                />
                {user.wallet.held_cents > 0 && (
                  <span className="text-slate-500">
                    ({t("walletPill.held")}{" "}
                    <Money
                      cents={user.wallet.held_cents}
                      currency={user.wallet.currency}
                    />
                    )
                  </span>
                )}
              </Link>
              <Link
                to="/wallet#topup"
                className="rounded-full bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
              >
                {t("nav.topUp")}
              </Link>
              <ProfileDropdown email={user.email} onLogout={onLogout} isAdmin={user.is_admin ?? false} />
              <LanguageSwitcher />
            </>
          ) : (
            <>
              <NavLink to="/login" className={navLinkClass}>
                {t("nav.login")}
              </NavLink>
              <NavLink
                to="/signup"
                className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
              >
                {t("nav.signUp")}
              </NavLink>
              <LanguageSwitcher />
            </>
          )}
        </nav>

        {/* Mobile hamburger button */}
        {user && (
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            aria-label="Open menu"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Mobile nav for unauthenticated users */}
        {!user && (
          <nav className="flex md:hidden items-center gap-3">
            <NavLink to="/login" className={navLinkClass}>
              {t("nav.login")}
            </NavLink>
            <NavLink
              to="/signup"
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("nav.signUp")}
            </NavLink>
            <LanguageSwitcher />
          </nav>
        )}
      </div>

      {/* Mobile drawer for authenticated users */}
      {user && (
        <MobileDrawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          user={user}
          onLogout={onLogout}
        />
      )}
    </header>
  );
}
